import React, { FC, useState } from 'react';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { BrandFilter } from '@marriott/mi-shop-components';
import { PropertyFacet, PropertySearchTermFacetBucket } from '@marriott/mi-groups-graphql';
import { CheckboxControlsList, RadiobuttonControlsList, baseVariables } from '@marriott/mi-ui-library';
import { facetsBaseConfig } from '../FilterBar/FilterBar';
import { AllFiltersModalProps } from './AllFiltersModal.types';
import { StyledAllFiltersModal } from './AllFiltersModal.styles';
import { FilterModal } from '../../../molecules/FilterModal';
import { getAmenitiesCheckboxControlsList } from '../AmenitiesFilterModal/AmenitiesFilterModal';
import { getAllBrands, getSelectableBrands } from '../BrandFilterModal/BrandFilterModal';
import { CheckboxControlsListControlProp } from '../AmenitiesFilterModal/AmenitiesFilterModal.types';
import { BrandItem, SearchType, SelectedFacets } from '../SearchResults.types';
import { useMediaQuery } from '../../../hooks';
import { DEFAULT_LANG, METERS_PER_KILOMETER, MILES_TO_METERS } from '../../../constants';
import { useDttStore, useSearchResultsStore } from '../../../store';
import { getCurrentLocale } from '../../../utils';

const formatDistanceControls = (distances: PropertySearchTermFacetBucket[], distanceMeasurementUnit: string) => {
  const locale = getCurrentLocale();
  const conversionFactor = locale !== DEFAULT_LANG ? METERS_PER_KILOMETER : MILES_TO_METERS;

  return distances.map(distance => {
    if (distance.code) {
      return distance;
    }

    const start = Math.round(Number(distance.start) / conversionFactor);
    const end = Math.round(Number(distance.end) / conversionFactor);
    const label = `${start} - ${end} ${distanceMeasurementUnit}`;

    return {
      code: distance.index?.toString(),
      count: distance.count?.toString(),
      label,
      disabled: !distance.count,
    };
  });
};

const getDistanceRadioControlsList = (
  defaultSelectedDistance: { label: string },
  distances: PropertySearchTermFacetBucket[],
  distanceMeasurementUnit: string
) => {
  const distanceControls = formatDistanceControls(distances, distanceMeasurementUnit);
  return [defaultSelectedDistance, ...distanceControls];
};

export const AllFiltersModal: FC<AllFiltersModalProps> = props => {
  const {
    filterType,
    labels,
    facetNames,
    facets,
    selectedFacets,
    brandCategories,
    showModal,
    isLoading,
    onFilterChange,
    onApply,
    onClear,
    onCloseModal,
  } = props;

  const isTabletAndAbove = useMediaQuery(baseVariables.mediaQuery.md);

  const { featureFlagLogo } = useDttStore();

  const [facetsSelected, setFacetsSelected] = useState<SelectedFacets>(selectedFacets);
  const searchType = useSearchResultsStore(state => state.searchType);

  const modifiedBrandCategories = getAllBrands(brandCategories, facets[PropertyFacet.BRANDS]);

  const amenitiesCheckboxControlsList = getAmenitiesCheckboxControlsList(facets[PropertyFacet.AMENITIES] ?? []);

  const defaultSelectedDistance = { label: `${labels.anyDistanceLabel}` };
  const distanceRadioControlsList = getDistanceRadioControlsList(
    defaultSelectedDistance,
    facets[PropertyFacet.DISTANCE] ?? [],
    labels.distanceMeasurementUnit
  );

  //handlers
  const handleChange = (facetType: PropertyFacet, selectedValues: PropertySearchTermFacetBucket[] | BrandItem[]) => {
    const isAnyDistance = (selectedValues as PropertySearchTermFacetBucket[]).some(
      distance => distance.label === labels.anyDistanceLabel
    );
    if (facetType === PropertyFacet.DISTANCE && isAnyDistance) {
      setFacetsSelected(prev => ({ ...prev, [facetType]: [] }));
    } else {
      setFacetsSelected(prev => ({ ...prev, [facetType]: selectedValues }));
    }
    onFilterChange(facetType, selectedValues);
  };

  const handleClear = () => {
    setFacetsSelected(facetsBaseConfig);
    onClear(filterType);
  };

  // function to get checkboxlist based on filter props
  const getCheckboxControlsList = (
    controls = [] as CheckboxControlsListControlProp[],
    defaultSelected: CheckboxControlsListControlProp[],
    onChange: (selectedValues: PropertySearchTermFacetBucket[]) => void,
    filterName?: string,
    isLastCategory?: boolean,
    customProps = {}
  ) => {
    return controls.length ? (
      <CheckboxControlsList
        key={filterName}
        title={filterName}
        name={filterName as string}
        controls={controls}
        defaultSelected={defaultSelected}
        onChange={selectedValues => onChange(selectedValues as PropertySearchTermFacetBucket[])}
        showSeparator={!isLastCategory}
        showMoreLabel={labels.showMoreLabel}
        showLessLabel={labels.showLessLabel}
        {...customProps}
      />
    ) : null;
  };

  // brand category
  const brandFilters = (
    <BrandFilter
      key={facetNames[PropertyFacet.BRANDS]}
      title={facetNames[PropertyFacet.BRANDS] as string}
      brandCategories={modifiedBrandCategories}
      defaultSelected={facetsSelected[PropertyFacet.BRANDS] ?? []}
      isHotelBrandSelectedFlag={true}
      onChange={selectedBrandItems => handleChange(PropertyFacet.BRANDS, getSelectableBrands(selectedBrandItems))}
      showSeparator={true}
      featureFlagLogo={featureFlagLogo}
    />
  );

  // distance category
  const distanceFilters = (
    <RadiobuttonControlsList
      key={facetNames[PropertyFacet.DISTANCE]}
      name={facetNames[PropertyFacet.DISTANCE] as string}
      title={facetNames[PropertyFacet.DISTANCE]}
      controls={distanceRadioControlsList}
      defaultSelected={
        facetsSelected[PropertyFacet.DISTANCE]?.length
          ? formatDistanceControls(facetsSelected[PropertyFacet.DISTANCE], labels.distanceMeasurementUnit)
          : [defaultSelectedDistance]
      }
      showSeparator={true}
      tabIndexForInput={-1}
      onChange={selectedValues =>
        handleChange(PropertyFacet.DISTANCE, selectedValues as PropertySearchTermFacetBucket[])
      }
    />
  );

  // used getCheckboxControlsList function to get other category filters
  const amenitiesFilter = getCheckboxControlsList(
    amenitiesCheckboxControlsList,
    facetsSelected[PropertyFacet.AMENITIES] ?? [],
    selectedValues => handleChange(PropertyFacet.AMENITIES, selectedValues),
    facetNames[PropertyFacet.AMENITIES],
    false,
    { colLength: isTabletAndAbove ? 6 : 12, enableShowMoreToggle: true, showMoreCount: 8 }
  );

  const transportationFilter = getCheckboxControlsList(
    facets[PropertyFacet.TRANSPORTATION_TYPES],
    facetsSelected[PropertyFacet.TRANSPORTATION_TYPES] ?? [],
    selectedValues => handleChange(PropertyFacet.TRANSPORTATION_TYPES, selectedValues),
    facetNames[PropertyFacet.TRANSPORTATION_TYPES]
  );

  const eventsFilter = getCheckboxControlsList(
    facets[PropertyFacet.MEETINGS_EVENTS],
    facetsSelected[PropertyFacet.MEETINGS_EVENTS] ?? [],
    selectedValues => handleChange(PropertyFacet.MEETINGS_EVENTS, selectedValues),
    facetNames[PropertyFacet.MEETINGS_EVENTS],
    false,
    { colLength: isTabletAndAbove ? 6 : 12 }
  );

  const statesFilter =
    searchType === SearchType.LOCATION_STATE ? (
      // eslint-disable-next-line react/jsx-no-useless-fragment
      <React.Fragment></React.Fragment>
    ) : (
      getCheckboxControlsList(
        facets[PropertyFacet.STATES],
        facetsSelected[PropertyFacet.STATES] ?? [],
        selectedValues => handleChange(PropertyFacet.STATES, selectedValues),
        facetNames[PropertyFacet.STATES]
      )
    );

  const citiesFilter = getCheckboxControlsList(
    facets[PropertyFacet.CITIES],
    facetsSelected[PropertyFacet.CITIES] ?? [],
    selectedValues => handleChange(PropertyFacet.CITIES, selectedValues),
    facetNames[PropertyFacet.CITIES]
  );

  const activitiesFilter = getCheckboxControlsList(
    facets[PropertyFacet.ACTIVITIES],
    facetsSelected[PropertyFacet.ACTIVITIES] ?? [],
    selectedValues => handleChange(PropertyFacet.ACTIVITIES, selectedValues),
    facetNames[PropertyFacet.ACTIVITIES],
    true
  );

  // all filters array
  const allfilters = [
    brandFilters,
    distanceFilters,
    amenitiesFilter,
    transportationFilter,
    eventsFilter,
    statesFilter,
    citiesFilter,
    activitiesFilter,
  ];

  return (
    <StyledAllFiltersModal>
      <FilterModal
        labels={labels}
        show={showModal}
        bodyCustomClass={'custom-scrollbar'}
        children={allfilters as JSX.Element[]}
        isLoading={isLoading}
        onCloseModal={onCloseModal}
        onClearSelectedFilters={handleClear}
        onApplySelectedFilters={() => onApply(filterType, facetsSelected, true)}
      />
    </StyledAllFiltersModal>
  );
};
